export type CardapioWaiterPayload = {
  copos: number;
  talheres: number;
  pratos: number;
  limpar_mesa: boolean;
  observacao: string;
  comanda_id?: number;
};

export type CardapioCloseAccountPayload = {
  modo: "mesa" | "individual" | "selecionados";
  pagamento: "no_local";
  item_ids: number[];
  comanda_id?: number;
  valor_total?: number;
};

export type CardapioMesaItemNotificationItem = {
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  observacao?: string | null;
};

export type CardapioMesaItemsPayload = {
  itens: CardapioMesaItemNotificationItem[];
  comanda_id?: number;
  origem?: string;
  origens?: string[];
  updated_at?: string;
};

export type CardapioCaixaNotification = {
  id: number;
  tipo: "waiter_request" | "close_account" | "mesa_items";
  cliente_nome: string;
  cliente_telefone: string;
  mesa: string;
  payload: CardapioWaiterPayload | CardapioCloseAccountPayload | CardapioMesaItemsPayload;
  status: string;
  created_at: string;
};

export function createIdempotencyKey(): string {
  return crypto.randomUUID?.() ?? `idem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function formatTelefoneNotificacao(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length === 11) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  return digits;
}

export function formatCardapioNotificationTitle(tipo: CardapioCaixaNotification["tipo"]): string {
  if (tipo === "waiter_request") return "Solicitação de garçom";
  if (tipo === "mesa_items") return "Novos itens";
  return "Fechar conta";
}

export function listWaiterSolicitacoes(payload: CardapioWaiterPayload): string[] {
  const solicitacoes: string[] = [];
  if (payload.copos > 0) solicitacoes.push(`${payload.copos}x Copo extra`);
  if (payload.talheres > 0) solicitacoes.push(`${payload.talheres}x Talheres extras`);
  if (payload.pratos > 0) solicitacoes.push(`${payload.pratos}x Prato extra`);
  if (payload.limpar_mesa) solicitacoes.push("Limpar mesa");
  return solicitacoes;
}

function formatMesaItemQuantidade(value: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.0001) return String(Math.round(n));
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

export function formatCardapioNotificationBody(notification: CardapioCaixaNotification): string {
  const lines: string[] = [];
  lines.push(`Mesa: ${notification.mesa}`);
  lines.push(`Nome: ${notification.cliente_nome}`);
  lines.push(`Telefone: ${formatTelefoneNotificacao(notification.cliente_telefone)}`);

  if (notification.tipo === "close_account") {
    return lines.join("\n");
  }

  if (notification.tipo === "mesa_items") {
    const payload = notification.payload as CardapioMesaItemsPayload;
    const itens = Array.isArray(payload.itens) ? payload.itens : [];
    if (itens.length > 0) {
      lines.push("");
      lines.push("Novos itens:");
      for (const item of itens) {
        const quantidade = Number(item.quantidade || 0);
        if (quantidade > 0 && item.nome_produto) {
          lines.push(`${formatMesaItemQuantidade(quantidade)}x ${item.nome_produto}`);
        }
      }
    }
    return lines.join("\n");
  }

  const payload = notification.payload as CardapioWaiterPayload;
  const solicitacoes = listWaiterSolicitacoes(payload);
  if (solicitacoes.length > 0) {
    lines.push("");
    lines.push("Solicitação:");
    lines.push(...solicitacoes);
  }
  if (payload.observacao.trim()) {
    lines.push("");
    lines.push("Observação:");
    lines.push(payload.observacao.trim());
  }
  return lines.join("\n");
}
